import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface WellSurveyState {
    loading: boolean;
    error: string | null;
    wellSurveyData: any;
    KOPOverride: number,
    MaxDLSAboveKOP: number | null,
    DepthOfMaxDLS: number | null,
    TransitionZone: number | null;
    TailPipeLength: number | null;
    TailPipeBottom: number | null;
    TailPipeInsideTZ: number | null;
    wellSurvey: {
        notes: string | null;
    },
    ESPLength: number;
    showWellDataInMainReport: boolean;
    BottomOfESPSensor: number;
    TailPipeDesander: number;
    isWellSurveyDataPersisted: boolean,
}


export const initialState: WellSurveyState = {
    loading: false,
    error: null,
    wellSurveyData: [],
    isWellSurveyDataPersisted: false,
    KOPOverride: 0,
    MaxDLSAboveKOP: null,
    DepthOfMaxDLS: null,
    TransitionZone: null,
    TailPipeLength: null,
    TailPipeBottom: null,
    TailPipeInsideTZ: null,
    wellSurvey: {
        notes: " "
    },
    ESPLength: 51.54,
    showWellDataInMainReport: false,
    BottomOfESPSensor: 7795,
    TailPipeDesander: 63.4,
};

const wellSurveySlice = createSlice({
    name: "wellSurvey",
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setWellSurveyData: (state, action: PayloadAction<any>) => {
            state.wellSurveyData = action.payload;
        },
        setWellKOPOverride: (state, action: PayloadAction<any>) => {
            state.KOPOverride = action.payload;
        },
        setMaxDLSAboveKOP: (state, action: PayloadAction<any>) => {
            state.MaxDLSAboveKOP = action.payload;
        },
        setDepthOfMaxDLS: (state, action: PayloadAction<any>) => {
            state.DepthOfMaxDLS = action.payload;
        },
        setTransitionZone: (state, action: PayloadAction<number>) => {
            state.TransitionZone = action.payload;
        },
        setTailPipeLength: (state, action: PayloadAction<number>) => {
            state.TailPipeLength = action.payload;
        },
        setTailPipeBottom: (state, action: PayloadAction<number>) => {
            state.TailPipeBottom = action.payload;
        },
        setTailPipeInsideTZ: (state, action: PayloadAction<number>) => {
            state.TailPipeInsideTZ = action.payload;
        },

        setNotes: (state, action) => {
            state.wellSurvey = {
                notes: action.payload
            };
        },
        setWellDataInMainReportFlag: (state, action) => {
            state.showWellDataInMainReport = action.payload;
        },
        fetchExistingData: (state) => {
            state.loading = true;
            state.error = null;
        },

        fetchExistingDataSuccess: (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            state.wellSurveyData = action.payload.map((item, index) => ({
                ...item,
                id: item.id || index + 1
            }));
        },

        fetchExistingDataError: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },
        setEspLength: (state, action: PayloadAction<number>) => {
            state.ESPLength = action.payload;
        },
        setBottomEspSensor: (state, action: PayloadAction<number>) => {
            state.BottomOfESPSensor = action.payload;
        },
        setTailPipeDesander: (state, action: PayloadAction<number>) => {
            state.TailPipeDesander = action.payload;
        },

        resetWellSurveyState: () => initialState,
        clearWellSurveyData: (state) => {
            state.wellSurveyData = [];
            state.KOPOverride = 0;
            state.MaxDLSAboveKOP = null;
            state.DepthOfMaxDLS = null;
            state.TransitionZone = null;
            state.TailPipeLength = null;
            state.TailPipeBottom = null;
            state.TailPipeInsideTZ = null;
            state.wellSurvey.notes = " ";
            state.isWellSurveyDataPersisted = false;
        },
        setWellSurveyDataPersisted: (state, action: PayloadAction<boolean>) => {
            state.isWellSurveyDataPersisted = action.payload;
        },

    },
});

export const { setTailPipeDesander, setBottomEspSensor, setEspLength, setLoading, setError, setWellSurveyData, resetWellSurveyState, setWellKOPOverride, setMaxDLSAboveKOP, setDepthOfMaxDLS, setTransitionZone, setTailPipeLength, setTailPipeBottom, setTailPipeInsideTZ, setNotes, fetchExistingData, fetchExistingDataSuccess, fetchExistingDataError, setWellDataInMainReportFlag, clearWellSurveyData, setWellSurveyDataPersisted } = wellSurveySlice.actions;
// Enhanced reducer to handle epic actions
const wellSurveyReducer = (state: WellSurveyState = initialState, action: any) => {
    // Handle RTK slice actions first
    const sliceState = wellSurveySlice.reducer(state, action);
    
    // Handle epic actions
    switch (action.type) {
        case 'wellSurvey/fetchData':
            return {
                ...sliceState,
                loading: true
            };
        case 'wellSurvey/fetchDataSuccess':
            return {
                ...sliceState,
                loading: false,
                wellSurveyData: action.payload,
                isWellSurveyDataPersisted: false
            };
        case 'wellSurvey/clearData':
            return {
                ...sliceState,
                loading: true
            };
        case 'wellSurvey/clearDataSuccess':
            return {
                ...sliceState,
                loading: false,
                wellSurveyData: [],
                KOPOverride: 0,
                MaxDLSAboveKOP: null,
                DepthOfMaxDLS: null,
                TransitionZone: null,
                TailPipeLength: null,
                TailPipeBottom: null,
                TailPipeInsideTZ: null,
                wellSurvey: { notes: " " },
                isWellSurveyDataPersisted: false
            };
        case 'wellSurvey/fetchDataError':
            return {
                ...sliceState,
                loading: false,
                error: action.payload
            };
        case 'wellSurvey/setWellSurveyDataPersisted':
            return {
                ...sliceState,
                isWellSurveyDataPersisted: action.payload
            };
        default:
            return sliceState;
    }
};

export default wellSurveyReducer;